// src/pages/atributos/Atributos.js
import React, { useEffect, useState, useCallback } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Badge from 'react-bootstrap/Badge'
import Accordion from 'react-bootstrap/Accordion'
import { toast } from 'react-toastify'
import api from 'api/api'

import { debounce } from 'utils/debounce'
import { LoadingSpinner } from 'components/LoadingSpinner'
import TopHeader from 'components/TopHeader'

import NuevoTipoAtributo from './NuevoTipoAtributo'
import EditarTipoAtributo from './EditarTipoAtributo'
import EliminarTipoAtributo from './EliminarTipoAtributo'
import NuevoValorAtributo from './NuevoValorAtributo'
import EditarValorAtributo from './EditarValorAtributo'
import EliminarValorAtributo from './EliminarValorAtributo'

import './atributos.css'

function Atributos() {
    const [loading, setLoading] = useState(false)
    const [tipos, setTipos] = useState([])
    const [numResultados, setNumResultados] = useState(0)

    const [busqueda, setBusqueda] = useState('')
    const [filtroBusqueda, setFiltroBusqueda] = useState('')
    const [filtroActivo, setFiltroActivo] = useState('todos') // 'todos', 'activos', 'inactivos'

    const [accionActual, setAccionActual] = useState({
        tipo: null,
        valor: null,
        accion: ''
    })

    // Cargar tipos de atributos
    const refreshTipos = useCallback(async () => {
        try {
            setLoading(true)

            const filtros = {}

            if (filtroBusqueda) {
                filtros.nombre = [filtroBusqueda]
            }

            if (filtroActivo === 'activos') {
                filtros.activo = true
            } else if (filtroActivo === 'inactivos') {
                filtros.activo = false
            }

            const { datos } = await api.post('a/ver-atributos-tipos', {
                filtros,
                desde: 0,
                cuantos: 100
            })

            setTipos(datos.tipos || [])
            setNumResultados(datos.numResultados || 0)
        } catch (error) {
            toast.error('Error al cargar los atributos')
            console.error(error)
        } finally {
            setLoading(false)
        }
    }, [filtroBusqueda, filtroActivo])

    // Debounce para búsqueda
    const handleBusqueda = (value) => {
        setFiltroBusqueda(value || '')
    }

    const debouncedFiltroBusqueda = React.useCallback(
        debounce(handleBusqueda, 400),
        []
    )

    useEffect(() => {
        debouncedFiltroBusqueda(busqueda)
    }, [busqueda, debouncedFiltroBusqueda])

    useEffect(() => {
        refreshTipos()
    }, [filtroBusqueda, filtroActivo, refreshTipos])

    // Acciones CRUD para tipos
    const crearTipo = async (data) => {
        try {
            const { msg } = await api.post('a/atributos-tipos', data)
            toast.success(msg)
            refreshTipos()
            cerrarAccion()
        } catch (error) {
            console.error(error)
            toast.error('Error al crear el tipo de atributo')
        }
    }

    const editarTipo = async (id, data) => {
        try {
            const { msg } = await api.put(`a/atributos-tipos/${id}`, data)
            toast.success(msg)
            refreshTipos()
            cerrarAccion()
        } catch (error) {
            console.error(error)
            toast.error('Error al actualizar el tipo de atributo')
        }
    }

    const eliminarTipo = async (id) => {
        try {
            const { msg } = await api.delete(`a/atributos-tipos/${id}`)
            toast.success(msg)
            refreshTipos()
            cerrarAccion()
        } catch (error) {
            console.error(error)
            toast.error(error.response?.data?.msg || 'Error al eliminar el tipo de atributo')
        }
    }

    // Acciones CRUD para valores
    const crearValor = async (tipoId, data) => {
        try {
            const { msg } = await api.post(`a/atributos-tipos/${tipoId}/valores`, data)
            toast.success(msg)
            refreshTipos()
            cerrarAccion()
        } catch (error) {
            console.error(error)
            toast.error('Error al crear el valor')
        }
    }

    const editarValor = async (valorId, data) => {
        try {
            const { msg } = await api.put(`a/atributos-valores/${valorId}`, data)
            toast.success(msg)
            refreshTipos()
            cerrarAccion()
        } catch (error) {
            console.error(error)
            toast.error('Error al actualizar el valor')
        }
    }

    const eliminarValor = async (valorId) => {
        try {
            const { msg } = await api.delete(`a/atributos-valores/${valorId}`)
            toast.success(msg)
            refreshTipos()
            cerrarAccion()
        } catch (error) {
            console.error(error)
            toast.error(error.response?.data?.msg || 'Error al eliminar el valor')
        }
    }

    // Toggle estado activo de un tipo
    const toggleTipoActivo = async (tipo) => {
        try {
            const { msg } = await api.put(`a/atributos-tipos/${tipo.id}`, {
                activo: !tipo.activo
            })
            toast.success(msg)
            refreshTipos()
        } catch (error) {
            console.error(error)
            toast.error('Error al cambiar el estado')
        }
    }

    // Toggle estado activo de un valor
    const toggleValorActivo = async (valor) => {
        try {
            const { msg } = await api.put(`a/atributos-valores/${valor.id}`, {
                activo: !valor.activo
            })
            toast.success(msg)
            refreshTipos()
        } catch (error) {
            console.error(error)
            toast.error('Error al cambiar el estado')
        }
    }

    const cerrarAccion = () => {
        setAccionActual({
            tipo: null,
            valor: null,
            accion: ''
        })
    }

    // Render
    return (
        <Container fluid id="atributos" className="vh-100">
            <TopHeader
                iconName="sliders"
                text="Atributos de Productos"
            />

            {/* Descripción */}
            <Row className="mb-3">
                <Col>
                    <p className="text-muted mb-0">
                        Configure los tipos de atributos (ej: Talla, Color) y sus valores posibles.
                        Estos atributos se pueden asignar a categorías y productos para definir variantes.
                    </p>
                </Col>
            </Row>

            {/* Filtros */}
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Group controlId="txtBusqueda">
                        <InputGroup>
                            <Form.Control
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                type="text"
                                placeholder="Buscar por nombre..."
                                aria-label="Buscar"
                            />
                            <InputGroup.Text>
                                <i className="fa fa-search"></i>
                            </InputGroup.Text>
                        </InputGroup>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Select
                        value={filtroActivo}
                        onChange={(e) => setFiltroActivo(e.target.value)}
                    >
                        <option value="todos">Todos los estados</option>
                        <option value="activos">Solo activos</option>
                        <option value="inactivos">Solo inactivos</option>
                    </Form.Select>
                </Col>
                <Col md={5} className="text-end">
                    <Button
                        variant="primary"
                        onClick={() => setAccionActual({ tipo: null, valor: null, accion: 'nuevoTipo' })}
                    >
                        <i className="fa fa-plus me-2"></i>
                        Nuevo tipo de atributo
                    </Button>
                </Col>
            </Row>

            {/* Contador de resultados */}
            <Row className="mb-3">
                <Col>
                    <small className="text-muted">
                        {loading ? (
                            <LoadingSpinner size="sm" />
                        ) : (
                            <>Total: <strong>{numResultados}</strong> tipo(s) de atributo</>
                        )}
                    </small>
                </Col>
            </Row>

            {/* Lista de tipos de atributos */}
            <Row>
                <Col>
                    {loading ? (
                        <div className="text-center py-5">
                            <LoadingSpinner />
                        </div>
                    ) : tipos.length === 0 ? (
                        <Card className="text-center py-5">
                            <Card.Body>
                                <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                                <p className="text-muted mb-3">No hay tipos de atributos configurados</p>
                                <Button
                                    variant="primary"
                                    onClick={() => setAccionActual({ tipo: null, valor: null, accion: 'nuevoTipo' })}
                                >
                                    <i className="fa fa-plus me-2"></i>
                                    Crear primer tipo de atributo
                                </Button>
                            </Card.Body>
                        </Card>
                    ) : (
                        <Accordion defaultActiveKey={tipos.length > 0 ? tipos[0].id.toString() : null}>
                            {tipos.map((tipo) => (
                                <Accordion.Item
                                    key={tipo.id}
                                    eventKey={tipo.id.toString()}
                                    className={!tipo.activo ? 'tipo-inactivo' : ''}
                                >
                                    <Accordion.Header>
                                        <div className="d-flex align-items-center justify-content-between w-100 me-3">
                                            <div className="d-flex align-items-center">
                                                <span className="tipo-nombre">{tipo.nombre}</span>
                                                {!tipo.activo && (
                                                    <Badge bg="secondary" className="ms-2">Inactivo</Badge>
                                                )}
                                                <Badge bg="info" className="ms-2">
                                                    {tipo.valores?.length || 0} valores
                                                </Badge>
                                            </div>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        {/* Descripción y acciones del tipo */}
                                        <div className="tipo-header mb-3">
                                            <Row className="align-items-center">
                                                <Col md={8}>
                                                    {tipo.descripcion && (
                                                        <p className="text-muted mb-0">
                                                            <small>{tipo.descripcion}</small>
                                                        </p>
                                                    )}
                                                </Col>
                                                <Col md={4} className="text-end">
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="me-1"
                                                        onClick={() => toggleTipoActivo(tipo)}
                                                        title={tipo.activo ? 'Desactivar' : 'Activar'}
                                                    >
                                                        <i className={`fa ${tipo.activo ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                    </Button>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="me-1"
                                                        onClick={() => setAccionActual({ tipo, valor: null, accion: 'editarTipo' })}
                                                        title="Editar tipo"
                                                    >
                                                        <i className="fa fa-pencil"></i>
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => setAccionActual({ tipo, valor: null, accion: 'eliminarTipo' })}
                                                        title="Eliminar tipo"
                                                    >
                                                        <i className="fa fa-trash"></i>
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Lista de valores */}
                                        <div className="valores-container">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="mb-0">Valores disponibles:</h6>
                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() => setAccionActual({ tipo, valor: null, accion: 'nuevoValor' })}
                                                >
                                                    <i className="fa fa-plus me-1"></i>
                                                    Agregar valor
                                                </Button>
                                            </div>

                                            {tipo.valores && tipo.valores.length > 0 ? (
                                                <div className="valores-lista">
                                                    {tipo.valores.map((valor) => (
                                                        <div
                                                            key={valor.id}
                                                            className={`valor-item ${!valor.activo ? 'valor-inactivo' : ''}`}
                                                        >
                                                            <span className="valor-nombre">{valor.nombre}</span>
                                                            <div className="valor-acciones">
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="p-0 me-2"
                                                                    onClick={() => toggleValorActivo(valor)}
                                                                    title={valor.activo ? 'Desactivar' : 'Activar'}
                                                                >
                                                                    <i className={`fa ${valor.activo ? 'fa-eye-slash' : 'fa-eye'} text-secondary`}></i>
                                                                </Button>
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="p-0 me-2"
                                                                    onClick={() => setAccionActual({ tipo, valor, accion: 'editarValor' })}
                                                                    title="Editar"
                                                                >
                                                                    <i className="fa fa-pencil text-primary"></i>
                                                                </Button>
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="p-0"
                                                                    onClick={() => setAccionActual({ tipo, valor, accion: 'eliminarValor' })}
                                                                    title="Eliminar"
                                                                >
                                                                    <i className="fa fa-trash text-danger"></i>
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-muted text-center py-3 mb-0">
                                                    <i className="fa fa-info-circle me-1"></i>
                                                    No hay valores definidos para este tipo
                                                </p>
                                            )}
                                        </div>
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    )}
                </Col>
            </Row>

            {/* Modales para CRUD */}

            {/* Nuevo tipo */}
            <NuevoTipoAtributo
                mostrar={accionActual.accion === 'nuevoTipo'}
                guardar={crearTipo}
                cancelar={cerrarAccion}
            />

            {/* Editar tipo */}
            {accionActual.tipo && accionActual.accion === 'editarTipo' && (
                <EditarTipoAtributo
                    mostrar={true}
                    tipo={accionActual.tipo}
                    guardar={(data) => editarTipo(accionActual.tipo.id, data)}
                    cancelar={cerrarAccion}
                />
            )}

            {/* Eliminar tipo */}
            {accionActual.tipo && accionActual.accion === 'eliminarTipo' && (
                <EliminarTipoAtributo
                    mostrar={true}
                    tipo={accionActual.tipo}
                    eliminar={() => eliminarTipo(accionActual.tipo.id)}
                    cancelar={cerrarAccion}
                />
            )}

            {/* Nuevo valor */}
            {accionActual.tipo && accionActual.accion === 'nuevoValor' && (
                <NuevoValorAtributo
                    mostrar={true}
                    tipo={accionActual.tipo}
                    guardar={(data) => crearValor(accionActual.tipo.id, data)}
                    cancelar={cerrarAccion}
                />
            )}

            {/* Editar valor */}
            {accionActual.valor && accionActual.accion === 'editarValor' && (
                <EditarValorAtributo
                    mostrar={true}
                    valor={accionActual.valor}
                    guardar={(data) => editarValor(accionActual.valor.id, data)}
                    cancelar={cerrarAccion}
                />
            )}

            {/* Eliminar valor */}
            {accionActual.valor && accionActual.accion === 'eliminarValor' && (
                <EliminarValorAtributo
                    mostrar={true}
                    valor={accionActual.valor}
                    tipo={accionActual.tipo}
                    eliminar={() => eliminarValor(accionActual.valor.id)}
                    cancelar={cerrarAccion}
                />
            )}
        </Container>
    )
}

export default Atributos